import React, { createContext, useContext, useState } from 'react';
import axios from 'axios';
import { createMessage } from './MessageModel';

const ChatContext = createContext();

export const useChat = () => useContext(ChatContext);

export const ChatProvider = ({ children }) => {
    const [username, setUsername] = useState(''); // Renamed to username
    const [messagesByRoom, setMessagesByRoom] = useState({});
    const [currentRoom, setCurrentRoom] = useState(null);
    const [loading, setLoading] = useState(false);
    const [language, setLanguage] = useState('en'); // Default language

    const resetCurrentRoom = () => {
        if (currentRoom) {
            setMessagesByRoom(prev => ({
                ...prev,
                [currentRoom.id]: []
            }));
            setCurrentRoom({ ...currentRoom, current_thread_id: null });
        }
    };

    const startConversation = async () => {
        try {
            await axios.post('/api/chat/start');
        } catch (error) {
            console.error('Error starting conversation:', error);
        }
    };

    const sendMessage = async (message) => {
        if (!currentRoom || loading) return;

        setLoading(true);
        const threadId = currentRoom.current_thread_id || null;
        const messageModel = createMessage(
            message.sender,
            message.receiver,
            message.content,
            threadId
        );

        const messageData = {
            ...messageModel,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            is_read: false,
            is_deleted: false,
            language: language // Include the currently selected language
        };

        // Add the user’s message locally, using the p_id from messageData.
        setMessagesByRoom(prev => {
            const currentRoomMessages = prev[currentRoom.id] || [];
            return {
                ...prev,
                [currentRoom.id]: [
                    ...currentRoomMessages,
                    {
                        author: message.sender,
                        text: message.content,
                        id: messageData.p_id,   // Use generated p_id
                        p_id: messageData.p_id  // Also store it explicitly if needed
                    }
                ]
            };
        });

        try {
            const apiUrl = currentRoom.api;
            console.log(apiUrl);
            const response = await axios.post(apiUrl, messageData, {
                headers: {
                    'Content-Type': 'application/json',
                }
            });
            // Expect the bot response to include a p_id, thread_id, and content.
            const botReply = response.data.content || 'No response from the bot.';
            const currentThreadId = response.data.thread_id;
            const botPId = response.data.p_id || (Date.now() + 1);  // Fallback if p_id is missing

            setCurrentRoom((prevRoom) => ({
                ...prevRoom,
                current_thread_id: currentThreadId,
            }));

            // Create bot message using the p_id from API response.
            const botMessage = {
                author: 'BOT',
                text: botReply,
                id: botPId,  // Use the p_id returned from the API
                p_id: botPId
            };

            // Add bot’s reply to the current room messages.
            setMessagesByRoom(prev => {
                const currentRoomMessages = prev[currentRoom.id] || [];
                return {
                    ...prev,
                    [currentRoom.id]: [...currentRoomMessages, botMessage]
                };
            });
        } catch (error) {
            console.error('Error sending message:', error);
            const errorMessage = 'Sorry, there was an error processing your request. Please try again later.';
            const botMessage = { author: 'BOT', text: errorMessage, id: Date.now() + 1 };

            setMessagesByRoom(prev => {
                const currentRoomMessages = prev[currentRoom.id] || [];
                return {
                    ...prev,
                    [currentRoom.id]: [...currentRoomMessages, botMessage]
                };
            });
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        setUsername('');
        // Optionally reset other states
        resetCurrentRoom();
        setLanguage('en');
    };

    const value = {
        username, // Updated to username
        setUsername,
        messagesByRoom,
        startConversation,
        sendMessage,
        currentRoom,
        setCurrentRoom,
        resetCurrentRoom,
        loading,
        language,
        setLanguage,
        handleLogout // Exposed handleLogout
    };

    return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};
